import { Modal } from './Modal'
import { VOLUME_CEILINGS, getAudioManager, getVolumeCeilings, getSettingsDefaults } from '../features/audio'
import { setRainAudioVolume } from '../features/rain/global-rain-manager'
import type { SceneData } from './StoryScene'

const SETTINGS_KEY = 'rychle-sipy-a-velikonocni-poklad:settings'

function reapplyAllVolumes(): void {
  const defaults = getSettingsDefaults()
  let music = defaults.music, speech = defaults.speech, sound = defaults.sound
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed?.sound) {
        music = parsed.sound.music ?? music
        speech = parsed.sound.speech ?? speech
        sound = parsed.sound.sound ?? sound
      }
    }
  } catch { /* ignore */ }

  const ceilings = getVolumeCeilings()
  const am = getAudioManager()
  am.setSpeechVolume(speech)
  am.setSoundVolume(sound)

  const soundNorm = (Math.max(0, Math.min(100, sound)) / 100) * ceilings.sound
  setRainAudioVolume(soundNorm)
  const appState = (window as any).__appState as any
  appState?.sfxManager?.setVolume(soundNorm)
  appState?.scrollSfxManager?.setVolume(soundNorm)

  const musicUserScale = Math.max(0, Math.min(100, music)) / 100
  appState?.musicManager?.setVolume(musicUserScale * ceilings.music)
  appState?.atmoManager?.setVolume(musicUserScale * ceilings.atmo)
}

/** Debug panel: data + callbacks only. Uses shared Modal for all UI. */
export class DebugModal {
  private onLoadGameStateCallback?: (sceneIndex: number) => void
  private scenes: SceneData[] = []

  constructor(private modal: Modal) {}

  setScenes(scenes: SceneData[]): void {
    this.scenes = scenes
  }

  setOnLoadGameState(callback: (sceneIndex: number) => void): void {
    this.onLoadGameStateCallback = callback
  }

  show(): void {
    this.modal.show(
      (dialog) => {
        dialog.appendChild(Modal.createHeader('God mode', () => this.modal.hide()))
        const content = Modal.createContent()

        // ── Scene loader section ──
        const section = Modal.createSectionWithDescription(
          'Načíst stav hry',
          'Vyber si scénu, která má být načtena jako aktuální stav hry.'
        )
        const options = this.scenes
          .map((scene, index) => {
            if (scene.type === 'choice') return null
            return { value: String(index), text: `${index + 1}. ${scene.id}${scene.type ? ` (${scene.type})` : ''}` }
          })
          .filter((o): o is { value: string; text: string } => o !== null)
        const { container: selectContainer, select } = Modal.createSelectControl({
          id: 'debug-scene-select',
          label: 'Scéna:',
          options,
        })
        section.appendChild(selectContainer)
        section.appendChild(
          Modal.createButton('Načíst stav hry', () => {
            const index = parseInt(select.value, 10)
            if (!isNaN(index) && this.onLoadGameStateCallback) {
              this.onLoadGameStateCallback(index)
              this.modal.hide()
            }
          })
        )
        content.appendChild(section)

        // ── Volume ceilings section ──
        content.appendChild(this.createVolumeCeilingsSection())

        dialog.appendChild(content)
      },
      {
        bodyScrollLock: true,
        initialFocus: () => this.modal.getDialog().querySelector('select, button') as HTMLElement | null,
      }
    )
  }

  hide(): void {
    this.modal.hide()
  }

  private createVolumeCeilingsSection(): HTMLDivElement {
    const section = Modal.createSectionWithDescription(
      'Audio Mix',
      'Maximální hlasitost každé kategorie (strop pro uživatelské posuvníky).'
    )

    const categories: Array<{ key: keyof typeof VOLUME_CEILINGS; label: string }> = [
      { key: 'speech', label: 'Řeč' },
      { key: 'atmo', label: 'Atmosféra' },
      { key: 'sound', label: 'Zvuky' },
      { key: 'music', label: 'Hudba' },
    ]

    for (const cat of categories) {
      const row = document.createElement('div')
      row.className = 'modal__control'
      row.style.display = 'flex'
      row.style.alignItems = 'center'
      row.style.gap = '0.75rem'

      const label = document.createElement('label')
      label.className = 'modal__control-label'
      label.textContent = cat.label
      label.style.minWidth = '5.5rem'

      const slider = document.createElement('input')
      slider.type = 'range'
      slider.min = '0'
      slider.max = '100'
      slider.value = String(Math.round(VOLUME_CEILINGS[cat.key] * 100))
      slider.className = 'modal__slider'
      slider.style.flex = '1'

      const valueDisplay = document.createElement('span')
      valueDisplay.style.minWidth = '3rem'
      valueDisplay.style.textAlign = 'right'
      valueDisplay.style.fontVariantNumeric = 'tabular-nums'
      valueDisplay.textContent = `${slider.value}%`

      Modal.syncRangeSliderFill(slider)

      slider.addEventListener('input', () => {
        Modal.syncRangeSliderFill(slider)
        const val = parseInt(slider.value, 10)
        VOLUME_CEILINGS[cat.key] = val / 100
        valueDisplay.textContent = `${val}%`
        reapplyAllVolumes()
      })

      row.appendChild(label)
      row.appendChild(slider)
      row.appendChild(valueDisplay)
      section.appendChild(row)
    }

    return section
  }
}
